import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { IoClose, IoHeart } from "react-icons/io5";
import { cardSetSlidesUrl } from "../../utils/urls";

// Components
import Button from "../button";
import ProgressBar from "../../components/progress";
import Content from "./components/Content";
import Actions from "./components/Actions";
import Navigation from "./components/Navigation";
import Finished from "./components/Finished";
import books from "../SmartNotePicker/assets/books.png";

// Styles
import styles from "./index.module.scss";

// Types
import { CardSet, Card } from "../../types";
import ProcessingModal from "../ProcessingModal";
import ThemeProvider from "../../utils/theme";
import { completeMaterial } from "../../api";

interface FlashcardReviewProps {
  cardSet: CardSet; // Rails object
  back_path?: string;
}

const MemoizedContent = React.memo(Content);
const MemoizedActions = React.memo(Actions);
const MemoizedNavigation = React.memo(Navigation);

const FlashcardReview: React.FC<FlashcardReviewProps> = (props: FlashcardReviewProps) => {
  const urlBackPath = new URLSearchParams(window.location.search).get('back_path');
  const backPath = props.back_path;
  const { t } = useTranslation();


  // State
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [cardProgress, setCardProgress] = useState<Record<string, true | false | undefined>>({});
  const [isFinished, setIsFinished] = useState<boolean>(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [startTime, setStartTime] = useState<number>(Date.now());
  const [endTime, setEndTime] = useState<number>();
  const [showHint, setShowHint] = useState(false);
  const [cardSet, setCardSet] = useState<CardSet>(props.cardSet);
  const { cards, processing, expired } = cardSet;

  // Track which cards were learned at the start of the session
  const [initialLearnedCardIds, setInitialLearnedCardIds] = useState<Set<string>>(new Set());

  // Refs
  const intervalRef = useRef<NodeJS.Timeout | null>(null);


  const fetchCardSet = async () => {
    const response = await fetch(`${cardSetSlidesUrl(cardSet.id, 'json')}`)
    const data = await response.json()
    const fetchedCardSet = data.card_set as CardSet;
    setCardSet(fetchedCardSet)
  }

  // Initialize learned card IDs when component mounts
  useEffect(() => {
    const learnedIds = new Set(cards.filter(card => card.status === 'learned').map(card => card.id));
    setInitialLearnedCardIds(learnedIds);
  }, [cards]);

  useEffect(() => {
    if (cardSet.processing) {
      intervalRef.current = setInterval(fetchCardSet, 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
  }, [cardSet.processing, cardSet.id])

  const handleFlip = useCallback(() => {
    setIsFlipped(!isFlipped);
    // Reset hint when flipping back to front
    if (isFlipped) {
      setShowHint(false);
    }
  }, [isFlipped]);

  // Handlers
  const handleQuit = async () => {
    const target = urlBackPath || backPath;
    if (target) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      Turbo.visit(target);
    } else {
      window.history.back();
    }
  };

  const handleComplete = useCallback(() => {
    setCardProgress({});
    setEndTime(undefined);
    setStartTime(Date.now());
    setIsFinished(false);
    setCurrentCardIndex(0);
  }, [backPath]);

  const handleNextCard = (answeredCorrectly?: boolean) => {
    setIsFlipped(false);
    setCardProgress(prev => ({ ...prev, [currentCard.id]: answeredCorrectly }));
    if (currentCardIndex < cards.length - 1) {
      setCurrentCardIndex(currentCardIndex + 1);
    } else {
      handleFinishReview();
    }
  };

  const handlePreviousCard = () => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex(currentCardIndex - 1);
    }
  };

  const handleFinishReview = useCallback(async () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setEndTime(Date.now());
    await fetchCardSet();
    setIsFinished(true);
    await completeMaterial(cardSet.material_id);
  }, []);

  // Computed values
  const currentCard = useMemo(() => {
    return cards[currentCardIndex];
  }, [cards, currentCardIndex]);

  const isLastCard = useMemo(() => {
    return currentCardIndex === cards.length - 1;
  }, [currentCardIndex, cards.length]);

  const handleCorrect = useCallback(() => {
    if (currentCard) {
      const url = `/cards/${currentCard.id}/correct`;
      fetch(url, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        }
      }).then(response => response.json()).then(data => {
        handleNextCard(true);
      });
    }
  }, [currentCard]);

  const handleIncorrect = useCallback(() => {
    if (currentCard) {
      const url = `/cards/${currentCard.id}/incorrect`;
      fetch(url, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        }
      }).then(response => response.json()).then(data => {
        handleNextCard(false);
      });
    }
  }, [currentCard]);

  // Render
  if (isFinished) {
    return (
      <div className='w-full rounded-lg flex flex-col items-center'>
        <Finished
          cardSet={cardSet}
          cardProgress={cardProgress}
          duration={endTime ? endTime - startTime : 0}
          initialLearnedCardIds={initialLearnedCardIds}
          onBack={handleQuit}
          onComplete={handleComplete}
        />
      </div>
    )
  }

  return (
    <ThemeProvider>
      <div className={`${styles.mainContainer} w-full rounded-lg`}>
        <ProcessingModal open={processing} message={t('processingModal.description')} />
        <div className={`${styles.headerContainer} dark:bg-stone-650 dark:border-gray-400`}>
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <img src={books} alt={`${cardSet.title} icon`} className={styles.icon} />
            </div>
            <div className={styles.titleSection}>
              <h1 className={`${styles.title} dark:text-white`} dir="auto">{cardSet.title}</h1>
              <span className={`${styles.subtitle} dark:text-white`}>{t('flashcardReview.flashcard')}</span>
            </div>

            <div className={styles.actions}>
              <Button
                type="icon"
                onClick={handleQuit}
                className={styles.quitButton}
              >
                <IoClose />
                <span className='hidden md:block'> {t('actions.quit')} </span>
              </Button>
            </div>
          </div>
          <div className={styles.progressSection}>
            <p className='text-sm text-gray-500 dark:text-gray-400 flex flex-row dark:text-white'>
              <span className='text-blue-500 dark:text-white'>{currentCardIndex + 1}</span>
              /{cards.length}
            </p>
            <ProgressBar
              progress={(currentCardIndex + 1) * 100 / cards.length}
            />
          </div>
        </div>

        <MemoizedContent
          card={currentCard}
          showHint={showHint}
          isFlipped={isFlipped}
          handleFlip={handleFlip}
          setShowHint={setShowHint}
          skippingHints={cardSet.skipping_hints}
        >
          <MemoizedActions
            card={currentCard}
            handleNextCard={handleNextCard}
            currentStatus={currentCard?.status}
            isFlipped={isFlipped}
            handleFlip={handleFlip}
          />
        </MemoizedContent>

        <MemoizedNavigation
          onNext={handleNextCard}
          onPrevious={handlePreviousCard}
          isFirstCard={currentCardIndex === 0}
          isLastCard={isLastCard}
          isFlipped={isFlipped}
          handleFlip={handleFlip}
          onCorrect={handleCorrect}
          onIncorrect={handleIncorrect}
        />
      </div>
    </ThemeProvider>
  )
};

export default FlashcardReview;
